import { z } from "zod";

/** Supported site locales (mirrors src/i18n/config.ts; kept dependency-free for the MCP bundle). */
export const PAGE_LOCALES = ["en", "ar", "pl", "uk", "de", "uz", "th", "hi", "es", "fr"] as const;
export type PageLocale = (typeof PAGE_LOCALES)[number];

export const BLOCK_TYPES = ["hero", "rich_text", "image", "cards", "cta", "faq", "media", "native"] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

/** Existing hardcoded marketing pages that are editable through draft revisions. */
export const NATIVE_PAGES = [
  { key: "home", path: "/", label: "Home" },
  { key: "about", path: "/about", label: "About us" },
  { key: "platform", path: "/platform", label: "Platform" },
  { key: "hardware", path: "/hardware", label: "Hardware" },
  { key: "modules", path: "/modules", label: "Modules overview" },
  { key: "tools-management", path: "/modules/tools-management", label: "Tools Management" },
  { key: "lean", path: "/lean", label: "Lean hub" },
  { key: "pricing", path: "/pricing", label: "Pricing (page copy)" },
  { key: "roi-calculator", path: "/roi-calculator", label: "ROI calculator" },
  { key: "resources", path: "/resources", label: "Resources" },
  { key: "mes-vs-erp", path: "/resources/mes-vs-erp-comparison", label: "MES vs ERP" },
  { key: "siri", path: "/siri", label: "SIRI assessment" },
  { key: "contact", path: "/contact", label: "Contact" },
  { key: "get-started", path: "/get-started", label: "Get started" },
  { key: "privacy", path: "/privacy", label: "Privacy notice", bodyMode: "replace" },
  { key: "terms", path: "/terms", label: "Terms of use", bodyMode: "replace" },
  { key: "copper-case-study", path: "/case-studies/copper-savings-die-management", label: "Copper case study" },
] as const;
export type NativePageKey = (typeof NATIVE_PAGES)[number]["key"];
/**
 * "text": built-in strings are edited per locale through text overrides.
 * "replace": the built-in body is plain legal JSX (not translation strings); editors replace it by hiding the
 * locked page_body block and adding reviewed blocks, so the legal text itself is fully editable after approval.
 */
export const nativeBodyMode = (key: string): "text" | "replace" =>
  ((NATIVE_PAGES as readonly { key: string; bodyMode?: string }[]).find((p) => p.key === key)?.bodyMode as "replace" | undefined) ?? "text";

/** Home page sections in their built-in order (each is an editable, orderable, stylable native block). */
export const HOME_SECTION_IDS = [
  "hero", "business-result-strip", "customer-logos", "ci-foundation", "lean-waste-table", "data-to-kaizen",
  "problem-gap", "solution-overview", "market-positioning", "start-small", "industry-templates",
  "industry-process-preview", "modules-grid", "hardware-block", "integrations-block", "business-outcomes",
  "lean-toolkit", "lean-kpi-dashboards", "daily-management", "six-sigma-dmaic", "tpm-support", "standard-work-5s",
  "onboarding-timeline", "security-enterprise", "case-studies-preview", "role-based-value", "opex-teams",
  "home-faq", "lean-final-cta", "contact-form",
];

/** Native interactive components a block may embed. Business logic (forms, calculators, tracking) stays inside them. */
export const NATIVE_BLOCK_COMPONENTS = [
  "page_body", "contact_form", "customer_logos", "modules_grid", "hardware_block", "integrations_block",
  ...HOME_SECTION_IDS.map((id) => `home:${id}`),
];

/** Routes a custom page slug may never use (custom pages live under /p/:slug; this keeps slugs unambiguous). */
export const RESERVED_SLUGS = [
  "admin", "login", "logout", "api", "assets", "static", "functions", "auth", "oauth", "p", "blog", "news", "case-studies",
  "industries", "partners", "about", "siri", "survey", "contact", "get-started", "platform", "hardware", "integrations",
  "modules", "lean", "pricing", "roi-calculator", "resources", "products", "glossary", "privacy", "terms", "sitemap",
  "robots", "reset-password", "favicon", ...PAGE_LOCALES,
];

const UNSAFE_TEXT = /(javascript|vbscript)\s*:|<\s*\/?\s*(script|iframe|object|embed|style|link|meta)\b|<[^>]*\son[a-z]+\s*=/i;

export function isSafeUrl(value: string): boolean {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (v !== value || !v || v.length > 2000) return false;
  if (/^(javascript|vbscript|data|file):/i.test(v)) return false;
  return /^(https?:\/\/[^/\s]+\S*|\/([^/\s]\S*)?|#[A-Za-z0-9_-]+|mailto:\S+|tel:[+0-9() -]+)$/.test(v);
}

const text = (max = 2000) => z.string().max(max).refine((s) => !UNSAFE_TEXT.test(s), "Unsafe content (scripts, event handlers or javascript: links are not allowed)");
const url = z.string().max(2000).refine((s) => s === "" || isSafeUrl(s), "Use https://, a site path (/...), #anchor, mailto: or tel:");

export const blockStyleSchema = z.object({
  background: z.enum(["default", "muted", "dark", "brand"]).optional(),
  padding: z.enum(["none", "sm", "md", "lg"]).optional(),
  align: z.enum(["left", "center"]).optional(),
  width: z.enum(["narrow", "normal", "wide"]).optional(),
  hideOn: z.enum(["none", "mobile", "desktop"]).optional(),
}).strict();

const propsByType = {
  hero: z.object({ eyebrow: text(200).optional(), heading: text(300), subheading: text(1000).optional(), image_url: url.optional(), image_alt: text(300).optional(), cta_label: text(80).optional(), cta_href: url.optional(), secondary_label: text(80).optional(), secondary_href: url.optional() }).strict(),
  rich_text: z.object({ heading: text(300).optional(), markdown: text(60000) }).strict(),
  image: z.object({ image_url: url, image_alt: text(300), caption: text(500).optional() }).strict(),
  cards: z.object({ heading: text(300).optional(), intro: text(1000).optional(), columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(), items: z.array(z.object({ title: text(200), body: text(1500).optional(), image_url: url.optional(), image_alt: text(300).optional(), href: url.optional() }).strict()).max(24) }).strict(),
  cta: z.object({ heading: text(300), body: text(1000).optional(), label: text(80), href: url }).strict(),
  faq: z.object({ heading: text(300).optional(), items: z.array(z.object({ q: text(500), a: text(4000) }).strict()).max(50) }).strict(),
  media: z.object({ video_url: url, caption: text(500).optional(), poster_url: url.optional() }).strict(),
  native: z.object({ component: z.string().refine((c) => NATIVE_BLOCK_COMPONENTS.includes(c), "Unknown native component"), label: text(200).optional(), locked: z.boolean().optional() }).strict(),
} as const;

const blockId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/, "Block ids are lowercase slugs (a-z, 0-9, -)");
const baseBlock = { id: blockId, visible: z.boolean().default(true), style: blockStyleSchema.default({}) };
export const blockSchema = z.discriminatedUnion("type", [
  z.object({ ...baseBlock, type: z.literal("hero"), props: propsByType.hero }).strict(),
  z.object({ ...baseBlock, type: z.literal("rich_text"), props: propsByType.rich_text }).strict(),
  z.object({ ...baseBlock, type: z.literal("image"), props: propsByType.image }).strict(),
  z.object({ ...baseBlock, type: z.literal("cards"), props: propsByType.cards }).strict(),
  z.object({ ...baseBlock, type: z.literal("cta"), props: propsByType.cta }).strict(),
  z.object({ ...baseBlock, type: z.literal("faq"), props: propsByType.faq }).strict(),
  z.object({ ...baseBlock, type: z.literal("media"), props: propsByType.media }).strict(),
  z.object({ ...baseBlock, type: z.literal("native"), props: propsByType.native }).strict(),
]);
export type PageBlock = z.infer<typeof blockSchema>;

const seoSchema = z.object({ title: text(200).optional(), description: text(400).optional(), og_image: url.optional(), noindex: z.boolean().optional() }).strict();
const localeKey = z.string().refine((l) => (PAGE_LOCALES as readonly string[]).includes(l), "Unsupported locale");
const translationSchema = z.object({
  seo: z.object({ title: text(200).optional(), description: text(400).optional() }).strict().optional(),
  blocks: z.record(blockId, z.record(z.string().regex(/^[a-z_]+(\.\d{1,2}\.[a-z_]+)?$/), text(60000))).optional(),
}).strict();

export const pageContentSchema = z.object({
  schema: z.literal(1),
  seo: seoSchema.default({}),
  blocks: z.array(blockSchema).max(200),
  translations: z.record(localeKey, translationSchema).default({}),
  /** Native pages: per-locale replacements of the page's built-in text, keyed by the English source string. */
  text_overrides: z.record(localeKey, z.record(z.string().max(2000), text(20000))).optional(),
  /** Native pages: replacements for built-in images, keyed by asset file name. */
  media_overrides: z.record(z.string().regex(/^[\w.-]{1,120}$/), z.object({ url, alt: text(300).optional() }).strict()).optional(),
}).strict().superRefine((c, ctx) => {
  const seen = new Set<string>();
  c.blocks.forEach((b, i) => {
    if (seen.has(b.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["blocks", i, "id"], message: `Duplicate block id ${b.id}` });
    seen.add(b.id);
  });
  if (JSON.stringify(c).length > 2_400_000) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Page content is too large" });
});
export type PageContent = z.infer<typeof pageContentSchema>;

export function validatePageContent(content: unknown): { ok: true; content: PageContent } | { ok: false; errors: string[] } {
  const r = pageContentSchema.safeParse(content);
  if (r.success) return { ok: true, content: r.data };
  return { ok: false, errors: r.error.issues.map((i) => `${i.path.join(".") || "content"}: ${i.message}`) };
}

export const customSlugSchema = z.string().min(3).max(80).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Use lowercase letters, digits and single hyphens")
  .refine((s) => !RESERVED_SLUGS.includes(s), "This route is reserved by the site");

export const emptyContent = (title = ""): PageContent => ({ schema: 1, seo: { title, description: "" }, blocks: [], translations: {} });

function setPath(props: Record<string, unknown>, path: string, value: string) {
  const parts = path.split(".");
  if (parts.length === 1) { if (typeof props[path] === "string" || props[path] === undefined) props[path] = value; return; }
  const [list, idx, field] = parts;
  const arr = props[list];
  if (Array.isArray(arr) && arr[+idx] && typeof arr[+idx] === "object") arr[+idx] = { ...arr[+idx], [field]: value };
}

/** Resolve a locale view: translated fields override source fields; untranslated fields keep the source text. */
export function localizeContent(content: PageContent, locale: string): PageContent {
  const tr = locale === "en" ? undefined : content.translations?.[locale as PageLocale];
  if (!tr) return content;
  return {
    ...content,
    seo: { ...content.seo, ...Object.fromEntries(Object.entries(tr.seo ?? {}).filter(([, v]) => v)) },
    blocks: content.blocks.map((b) => {
      const patch = tr.blocks?.[b.id];
      if (!patch) return b;
      const props: Record<string, unknown> = JSON.parse(JSON.stringify(b.props));
      for (const [path, value] of Object.entries(patch)) if (value) setPath(props, path, value);
      return { ...b, props } as PageBlock;
    }),
  };
}

type TranslationPatch = { seo?: { title?: string; description?: string }; blocks?: Record<string, Record<string, string>> };
/** Merge a translation patch for one locale without touching other locales or unrelated fields. */
export function mergeTranslationPatch<T extends { translations?: Record<string, TranslationPatch> }>(content: T, locale: string, patch: TranslationPatch): T {
  const current = content.translations?.[locale] ?? {};
  const blocks: Record<string, Record<string, string>> = { ...(current.blocks ?? {}) };
  for (const [id, fields] of Object.entries(patch.blocks ?? {})) blocks[id] = { ...(blocks[id] ?? {}), ...fields };
  return {
    ...content,
    translations: { ...(content.translations ?? {}), [locale]: { ...current, ...(patch.seo || current.seo ? { seo: { ...(current.seo ?? {}), ...(patch.seo ?? {}) } } : {}), blocks } },
  };
}

/** Field paths of a block that carry translatable text (used by the editor and MCP discovery). */
export function translatableFields(block: PageBlock): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(block.props as Record<string, unknown>)) {
    if (typeof v === "string" && !/(url|href)$/.test(k) && k !== "component") out.push(k);
    if (Array.isArray(v)) v.forEach((item, i) => { if (item && typeof item === "object") for (const [f, fv] of Object.entries(item)) if (typeof fv === "string" && !/(url|href)$/.test(f)) out.push(`${k}.${i}.${f}`); });
  }
  return out;
}

/** Human-readable catalogue for MCP clients and the admin "add block" menu. */
export const BLOCK_CATALOG: Record<BlockType, { description: string; props: string }> = {
  hero: { description: "Large heading with optional image and up to two buttons", props: "eyebrow?, heading, subheading?, image_url?, image_alt?, cta_label?, cta_href?, secondary_label?, secondary_href?" },
  rich_text: { description: "Markdown text section", props: "heading?, markdown" },
  image: { description: "Single image with alt text", props: "image_url, image_alt, caption?" },
  cards: { description: "Grid of cards (2-4 columns)", props: "heading?, intro?, columns?, items[{title, body?, image_url?, image_alt?, href?}]" },
  cta: { description: "Call-to-action band", props: "heading, body?, label, href" },
  faq: { description: "Questions and answers", props: "heading?, items[{q, a}]" },
  media: { description: "Embedded video (YouTube/Vimeo https link) or video file", props: "video_url, caption?, poster_url?" },
  native: { description: "Built-in interactive component (forms, calculator, existing home sections)", props: `component (one of: ${["page_body", "contact_form", "customer_logos", "modules_grid", "hardware_block", "integrations_block", "home:<section-id>"].join(", ")}), label?, locked?` },
};
export const STYLE_OPTIONS = { background: ["default", "muted", "dark", "brand"], padding: ["none", "sm", "md", "lg"], align: ["left", "center"], width: ["narrow", "normal", "wide"], hideOn: ["none", "mobile", "desktop"] };
